import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {AmchartsService} from '../../shared/_services/amcharts.service';
import {takeUntil} from 'rxjs/operators';
import {AssetModel} from '../_models/asset.model';
import {PosService} from '../_services/pos.service';
import {PosDataModel, PosModel} from '../_models/pos.model';

@Component({
  selector: 'app-position-chart',
  templateUrl: './position-chart.component.html',
  styleUrls: ['./position-chart.component.scss'],
})
export class PositionChartComponent
  implements OnInit, AfterViewInit, OnDestroy {

  @Input() country: AssetModel;

  @ViewChild('pos_chartdiv') chartDiv: ElementRef;

  private pos: PosModel;
  private chart: am4charts.XYChart;
  private posOperators: string[] = [];
  private chartColors: string[] = [
    '#0000e6',
    '#00cc66',
    '#cc0033',
    '#00cc99',
    '#9900cc',
    '#cc9900',
  ];

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private amChartService: AmchartsService,
    private posService: PosService,
    private ngZone: NgZone
  ) {
  }

  ngOnInit() {
    this.getPos();
  }

  ngAfterViewInit() {
    this.updateChartData();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getPos(): void {
    this.posService
      .onNewPos()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((pos) => {
        if (pos.length) {
          this.pos = pos.find((i) => i.country === this.country.label) || {
            country: this.country.label,
            posData: [],
          };

          // Filter by customer/operator (engie, delesto, etc)
          const selectedOperators = this.country.customers
            .filter((data) => data.selected === true)
            .map((a) => a.label.toLocaleLowerCase());

          this.pos.posData = this.pos.posData.filter((data) =>
            selectedOperators.includes(data.operator)
          );

          // Retrieve operators
          this.posOperators = [];
          this.pos.posData.forEach((data) => {
            const operator = data.operator;
            if (operator && !this.posOperators.includes(operator)) {
              this.posOperators.push(operator);
            }
          });

          // Put Engie operator at the beginning
          // in order to show it first on the graph
          const engieOperator = 'engie';
          if (this.posOperators && this.posOperators.includes(engieOperator)) {
            this.posOperators = this.posOperators.filter(
              (el) => el !== engieOperator
            );
            this.posOperators.unshift(engieOperator);
          }

          this.updateChartData();
        }
      });
  }

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  private mutateData(posData: PosDataModel[]): any[] {
    // Add data pre-processor to mutate the source
    // data in a way that can be used by chart

    const dates = {};
    const data = [];

    posData.forEach((row) => {
      const operator = row.operator;

      if (dates[row.ts] === undefined) {
        dates[row.ts] = {date: row.ts};
        data.push(dates[row.ts]);
      }

      // Add physical & economic position
      dates[row.ts]['ts'] = row.ts;
      dates[row.ts]['operator'] = row.operator;
      dates[row.ts][`physical${operator}`] = row.phPos;
      dates[row.ts][`economic${operator}`] = row.ecPos;
    });

    return data;
  }

  private updateChartData(): void {
    if (this.posOperators.length > 0) {
      this.createChart();
    }
    if (this.chart && this.pos) {
      this.ngZone.runOutsideAngular(() => {
        this.chart.data = this.mutateData(this.pos?.posData);
      });
    }
  }

  // ********************************************************************************************************
  // UI
  // ********************************************************************************************************

  private createChart(): void {
    if (!this.chart && this.chartDiv?.nativeElement) {
      this.ngZone.runOutsideAngular(() => {
        // CHART
        const chart = am4core.create(
          'pos-chart__chart_' + this.country.label,
          am4charts.XYChart
        );
        this.amChartService.setChartDefaults(chart, true);
        chart.dateFormatter.inputDateFormat = 'yyyy-MM-dd HH:mm';
        chart.paddingRight = 34; // to make room for wide date labels of date axis
        chart.cursor = new am4charts.XYCursor();

        // AXES
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        this.amChartService.setDateAxis(
          dateAxis,
          'date',
          true,
          false,
          true,
          80
        );
        dateAxis.tooltipDateFormat = 'yyyy-MM-dd HH:mm';
        dateAxis.dateFormats.setKey('day', 'MMM dd \'(\'EEE\')\'');
        const valueAxis1 = chart.yAxes.push(new am4charts.ValueAxis());
        this.amChartService.setValueAxis(valueAxis1, 'MW');

        // STACKED LINES
        for (const positionType of ['physical', 'economic']) {
          this.posOperators.forEach((operator, index) => {
            // Chart series
            const seriesPh = chart.series.push(new am4charts.StepLineSeries());
            this.amChartService.setSeries(
              seriesPh,
              'date',
              `${positionType} - ${operator.toUpperCase()}`,
              'ts',
              `${positionType}${operator}`,
              null,
              this.chartColors[index]
            );

            // Series parameteters
            seriesPh.yAxis = valueAxis1;
            seriesPh.xAxis = dateAxis;

            if (positionType === 'physical') {
              seriesPh.fillOpacity = 0.1;
              seriesPh.strokeWidth = 0.3;
            } else {
              seriesPh.fillOpacity = 0.6;
              seriesPh.strokeWidth = 0;
            }

            // Stack only the same kind of line (physic vs economic)
            seriesPh.stacked = index > 0;
            seriesPh.stackToNegative = false;
          });
        }

        this.chart = chart;
      });
    }
  }
}
